import { useCallback, useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';

const FILTER_OPTIONS = [
  { value: 'success_any', label: 'Успешно решённые (хотя бы один успех)' },
  { value: 'no_attempts', label: 'Без попыток' },
  { value: 'no_success_in_months', label: 'Нет успеха за выбранный период' },
  { value: 'attempted_never_succeeded', label: 'Пробовали, но ни одной успешной попытки' },
];

const MONTH_OPTIONS = [1, 2, 3, 5];

/** Соответствует tasks.models.Tag.TYPE_CHOICES */
const TAG_TYPE_OPTIONS = [
  { value: 'topic', label: 'Тема' },
  { value: 'collection', label: 'Подборка' },
  { value: 'exam', label: 'Экзамен' },
  { value: 'grade', label: 'Класс' },
  { value: 'difficulty', label: 'Сложность' },
  { value: 'year', label: 'Год' },
  { value: 'status', label: 'Статус' },
];

/**
 * Список задач каталога по тегу с фильтром прогресса ученика.
 * API: GET /api/teacher/students/:id/tasks-by-tag/
 */
export default function TasksByTagProgressPanel({ studentId, suppressBuiltInTitle = false }) {
  const tagDatalistId = useId();
  const [tagType, setTagType] = useState('topic');
  const [tagsForType, setTagsForType] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagInput, setTagInput] = useState('');
  const [progressFilter, setProgressFilter] = useState('success_any');
  const [months, setMonths] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function loadTags() {
      setTagsLoading(true);
      try {
        const res = await api.get('/tasks/tags/', { params: { type: tagType } });
        const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
        if (!cancelled) {
          setTagsForType(list);
          setTagInput(list[0]?.slug ?? '');
        }
      } catch {
        if (!cancelled) {
          setTagsForType([]);
          setTagInput('');
        }
      } finally {
        if (!cancelled) setTagsLoading(false);
      }
    }
    loadTags();
    return () => {
      cancelled = true;
    };
  }, [tagType]);

  const tagSlugTrimmed = tagInput.trim();

  const loadPage = useCallback(
    async (nextPage, append) => {
      const sid = Number(studentId);
      if (!sid || Number.isNaN(sid)) return;
      if (!tagSlugTrimmed) {
        setError('Укажите тег (slug) или выберите из списка.');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const params = {
          tag: tagSlugTrimmed,
          filter: progressFilter,
          page: nextPage,
        };
        if (progressFilter === 'no_success_in_months') {
          params.months = months;
        }
        const res = await api.get(`/teacher/students/${sid}/tasks-by-tag/`, { params });
        const newRows = Array.isArray(res.data?.results) ? res.data.results : [];
        setRows((prev) => (append ? [...prev, ...newRows] : newRows));
        setTotalCount(typeof res.data?.count === 'number' ? res.data.count : null);
        setHasMore(Boolean(res.data?.next));
        setPage(nextPage);
      } catch (e) {
        const msg = e?.response?.data?.detail;
        setError(typeof msg === 'string' ? msg : 'Не удалось загрузить список.');
        if (!append) {
          setRows([]);
          setTotalCount(null);
        }
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [studentId, tagSlugTrimmed, progressFilter, months],
  );

  function handleShow() {
    void loadPage(1, false);
  }

  function handleLoadMore() {
    if (!hasMore || loading) return;
    void loadPage(page + 1, true);
  }

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const typeLabel = TAG_TYPE_OPTIONS.find((o) => o.value === tagType)?.label ?? tagType;

  if (!studentId || Number.isNaN(Number(studentId))) {
    return null;
  }

  return (
    <div
      className={`recent-attempts-block tasks-by-tag-progress-panel ${suppressBuiltInTitle ? 'tasks-by-tag-embedded' : ''}`.trim()}
      style={suppressBuiltInTitle ? { marginTop: 0 } : { marginTop: '0.75rem' }}
    >
      {!suppressBuiltInTitle && (
        <>
          <h4>Задачи по теме (тегу)</h4>
          <p className="text-secondary" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            Задачи из публичного каталога с выбранным тегом и фильтром по сохранённым попыткам и проверкам (редактор, вердикт преподавателя, judge).
          </p>
        </>
      )}

      {tagsLoading ? (
        <p className="text-secondary">Загрузка тегов…</p>
      ) : (
        <div className="form-group" style={{ display: 'grid', gap: '0.5rem' }}>
          <label className="text-secondary" style={{ fontSize: '0.85rem' }} htmlFor="tasks-by-tag-type-select">
            Тип тега
          </label>
          <select
            id="tasks-by-tag-type-select"
            className="form-select"
            value={tagType}
            onChange={(e) => setTagType(e.target.value)}
          >
            {TAG_TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <label className="text-secondary" style={{ fontSize: '0.85rem' }} htmlFor="tasks-by-tag-slug-input">
            Тег (slug)
          </label>
          <input
            id="tasks-by-tag-slug-input"
            type="text"
            className="form-input"
            list={tagDatalistId}
            placeholder="Введите или выберите slug…"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            autoComplete="off"
          />
          <datalist id={tagDatalistId}>
            {tagsForType.map((t) => (
              <option key={t.slug} value={t.slug} label={`${t.name} (${t.slug})`} />
            ))}
          </datalist>

          {tagsForType.length === 0 && !tagsLoading && (
            <p className="text-secondary" style={{ fontSize: '0.8rem' }}>
              Нет активных тегов типа «{typeLabel}». Можно ввести slug вручную.
            </p>
          )}

          <label className="text-secondary" style={{ fontSize: '0.85rem' }} htmlFor="tasks-by-tag-filter">
            Фильтр
          </label>
          <select
            id="tasks-by-tag-filter"
            className="form-select"
            value={progressFilter}
            onChange={(e) => setProgressFilter(e.target.value)}
          >
            {FILTER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {progressFilter === 'no_success_in_months' && (
            <>
              <label className="text-secondary" style={{ fontSize: '0.85rem' }} htmlFor="tasks-by-tag-months">
                Период без успеха
              </label>
              <select
                id="tasks-by-tag-months"
                className="form-select"
                value={months}
                onChange={(e) => setMonths(Number(e.target.value))}
              >
                {MONTH_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {m} {m === 1 ? 'месяц' : m < 5 ? 'месяца' : 'месяцев'}
                  </option>
                ))}
              </select>
            </>
          )}

          <button type="button" className="btn btn-xs btn-outline" disabled={loading} onClick={handleShow}>
            {loading ? 'Загрузка…' : 'Показать'}
          </button>
        </div>
      )}

      {error && <div className="alert alert-error" style={{ marginTop: '0.5rem' }}>{error}</div>}

      {totalCount != null && (
        <p className="text-secondary" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
          Найдено: {totalCount}
        </p>
      )}

      {rows.length > 0 && (
        <ul className="recent-attempts-list" style={{ marginTop: '0.5rem' }}>
          {rows.map((row) => (
            <li key={row.id}>
              <Link to={`/work/${row.url_ref}`} className="link-like">
                {row.title}
              </Link>
              <div className="text-secondary" style={{ fontSize: '0.75rem', marginTop: '0.15rem' }}>
                Успех: {fmt(row.last_success_at)} · Любая попытка: {fmt(row.last_any_attempt_at)}
              </div>
            </li>
          ))}
        </ul>
      )}

      {!loading && rows.length === 0 && totalCount === 0 && (
        <p className="text-secondary" style={{ marginTop: '0.5rem' }}>
          Нет задач по этому фильтру.
        </p>
      )}

      {hasMore && (
        <button type="button" className="btn btn-xs btn-outline" style={{ marginTop: '0.5rem' }} disabled={loading} onClick={handleLoadMore}>
          {loading ? '…' : 'Загрузить ещё'}
        </button>
      )}
    </div>
  );
}
